import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { IAppState } from '../redux/store/types';
import { ISaasTenantSearchResult } from '../redux/saasTenants/types';
import {
  prepareSearchSaasTenantNamesCommandAction,
  setSaasTenantSearchResultsAction,
  setSaasTenantSearchTooManyResultsAction,
} from '../redux/saasTenants/actions';

/**
 * SaaS tenant multi-select with debounced server-side search.
 *
 * Behavior:
 * - Maintains local UI state for the search text and dropdown visibility.
 * - Dispatches a debounced search action when input length reaches `minSearchChars`.
 * - Allows selecting/unselecting tenants from the dropdown; selection is controlled by parent via `selectedSaasTenantIds`.
 */
class SaasTenantSelectorBase extends React.Component<ISaasTenantSelectorProps, ISaasTenantSelectorState> {
  private searchTimeout: NodeJS.Timeout | null = null;
  private readonly minSearchChars = 2;

  constructor(props: ISaasTenantSelectorProps) {
    super(props);
    this.state = {
      searchText: '',
      showDropdown: false,
    };
  }

  /**
   * Handles changes to the search input.
   *
   * Side-effects:
   * - Updates local state (`searchText`, `showDropdown`).
   * - Debounces a Redux dispatch to search tenant names.
   * - Clears search results when input is shorter than `minSearchChars`.
   */
  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    this.setState({ searchText, showDropdown: searchText.length >= this.minSearchChars });

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Only search if minSearchChars+ characters
    if (searchText.length >= this.minSearchChars) {
      // Debounce the search by 300ms
      this.searchTimeout = setTimeout(() => {
        this.props.dispatch(prepareSearchSaasTenantNamesCommandAction(searchText));
      }, 300);
    } else {
      // Clear search results if less than 2 characters
      this.props.dispatch(setSaasTenantSearchResultsAction([]));
      this.props.dispatch(setSaasTenantSearchTooManyResultsAction(false));
    }
  };

  /**
   * Toggles a tenant ID in the selection and notifies the parent component.
   *
   * @param tenantId Tenant UUID to add/remove.
   */
  handleTenantToggle = (tenantId: string) => {
    const { selectedSaasTenantIds, onSelectionChange } = this.props;

    let newSelection: string[];
    if (selectedSaasTenantIds.includes(tenantId)) {
      // Remove tenant from selection
      newSelection = selectedSaasTenantIds.filter((id) => id !== tenantId);
    } else {
      // Add tenant to selection
      newSelection = [...selectedSaasTenantIds, tenantId];
    }

    onSelectionChange(newSelection);
  };

  /**
   * Shows the dropdown when the input is focused and the search text is long enough.
   */
  handleInputFocus = () => {
    if (this.state.searchText.length >= this.minSearchChars) {
      this.setState({ showDropdown: true });
    }
  };

  /**
   * Hides the dropdown shortly after blur to allow option clicks to register.
   */
  handleInputBlur = () => {
    // Delay hiding dropdown to allow clicks on options
    setTimeout(() => {
      this.setState({ showDropdown: false });
    }, 200);
  };

  /**
   * Clears the pending debounce timer when the component unmounts.
   */
  componentWillUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  /**
   * Renders:
   * - A label
   * - Selected tenant tags (if any)
   * - Search input
   * - Dropdown results (or empty/too-many states)
   */
  render() {
    const { t, searchResults, selectedSaasTenantIds, sharedSaasTenantIdsAndNames, searchTooManyResults } = this.props;
    const { searchText, showDropdown } = this.state;

    // Get selected tenant names for display
    const allTenantsNamesMap = new Map<string, string>();
    if (sharedSaasTenantIdsAndNames) sharedSaasTenantIdsAndNames.forEach((tenant) => allTenantsNamesMap.set(tenant.saas_tenant_id, tenant.name));
    searchResults.forEach((tenant) => allTenantsNamesMap.set(tenant.saas_tenant_id, tenant.name));

    const selectedTenantsForDisplay = selectedSaasTenantIds.map((id) => ({ saas_tenant_id: id, name: allTenantsNamesMap.get(id) }));

    return (
      <div className='saas-tenant-selector'>
        <div className='selector-label'>{t('saasTenants.shareWith')}:</div>

        {/* Selected tenants display */}
        {selectedTenantsForDisplay.length > 0 && (
          <div className='selected-tenants'>
            {selectedTenantsForDisplay.map((tenant) => (
              <span key={tenant.saas_tenant_id} className='selected-tenant-tag'>
                {tenant.name}
                <button type='button' onClick={() => this.handleTenantToggle(tenant.saas_tenant_id)} className='remove-tenant-btn'>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search input */}
        <div className='search-container'>
          <input
            type='text'
            autoFocus={true}
            value={searchText}
            onChange={this.handleSearchChange}
            onFocus={this.handleInputFocus}
            onBlur={this.handleInputBlur}
            placeholder={t('saasTenants.searchPlaceholder', 'Search tenants (min 3 characters)...')}
            className='tenant-search-input'
          />

          {/* Dropdown with search results */}
          {showDropdown && !searchTooManyResults && searchResults.length > 0 && (
            <div className='search-dropdown' onMouseDown={(e) => e.preventDefault()}>
              {searchResults.map((tenant) => (
                <div
                  key={tenant.saas_tenant_id}
                  className={`dropdown-item ${selectedSaasTenantIds.includes(tenant.saas_tenant_id) ? 'selected' : ''}`}
                  onClick={() => this.handleTenantToggle(tenant.saas_tenant_id)}
                >
                  <span className='tenant-name'>{tenant.name}</span>
                  {selectedSaasTenantIds.includes(tenant.saas_tenant_id) && <span className='checkmark'>✓</span>}
                </div>
              ))}
            </div>
          )}

          {/* Too many results message */}
          {showDropdown && searchText.length >= this.minSearchChars && searchTooManyResults && (
            <div className='search-dropdown' onMouseDown={(e) => e.preventDefault()}>
              <div className='dropdown-item no-results'>{t('saasTenants.tooManyResults', 'Too many results — type another character')}</div>
            </div>
          )}

          {/* No results message */}
          {showDropdown && searchText.length >= this.minSearchChars && !searchTooManyResults && searchResults.length === 0 && (
            <div className='search-dropdown' onMouseDown={(e) => e.preventDefault()}>
              <div className='dropdown-item no-results'>{t('saasTenants.noResults', 'No tenants found')}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

interface ISaasTenantSelectorState {
  searchText: string;
  showDropdown: boolean;
}

interface ISaasTenantSelectorProps {
  t: (key: string, options?: any) => string;
  searchResults: ISaasTenantSearchResult[];
  searchTooManyResults: boolean;
  selectedSaasTenantIds: string[];
  sharedSaasTenantIdsAndNames?: { saas_tenant_id: string; name: string }[];
  onSelectionChange: (selectedIds: string[]) => void;
  dispatch: any;
}

const mapStateToProps = (state: IAppState) => ({
  searchResults: state.saasTenants.searchResults,
  searchTooManyResults: state.saasTenants.searchTooManyResults,
});

export const SaasTenantSelector = connect(mapStateToProps)(withTranslation()(SaasTenantSelectorBase));
